import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  hasDecision,
  acceptAll,
  rejectAll,
  setConsent,
  getConsent,
  CONSENT_OPEN_EVENT,
} from '../lib/consent'

// First-party cookie consent banner — no third-party CMP. Strict opt-in:
// the banner shows until the visitor makes a choice, and analytics stays
// OFF (nothing stored) unless they accept.
//
// ⚠️ V1 carried a first-party cookieless tracker (hooks/useTracking.js) that
// this banner’s `analytics` category gated. That tracker posted to /api/track/*,
// which is NOT part of the V2 API, so it was dropped in the port — see
// lib/consent.js for what that leaves the toggle wired to.
//
// Two surfaces:
//   • Banner  — "Reject optional" / "Accept analytics" side by side, then the
//     "Cookie Policy" and "Cookie settings" text links on one row beneath them.
//   • Manage  — small panel listing each category with a toggle. Reachable
//     from the banner OR re-opened any time via the footer "Cookie
//     settings" link (CONSENT_OPEN_EVENT), so withdrawal is as easy as
//     granting (UK GDPR).
//
// Equal-weight Accept / Reject buttons (no dark-pattern nudging) per ICO
// guidance. Mounted in App.jsx on every non-admin host.

// The banner's two answers share ONE class string, so they cannot drift apart:
// the house outline pill (ds Button `outline`), sized for a 380px card.
const CHOICE =
  'rounded-full border border-ink/15 px-3 py-2.5 text-[15px] font-semibold text-ink ' +
  'transition-colors hover:border-ink/35 hover:bg-ink/[0.03] sm:px-4'
const LINK = 'text-link underline-offset-4 hover:underline'

export default function CookieConsent() {
  // Show the banner on first load only when there’s no current decision.
  const [showBanner, setShowBanner] = useState(() => !hasDecision())
  const [manageOpen, setManageOpen] = useState(false)
  const headingRef = useRef(null)
  // Analytics toggle state for the manage panel — seeded from any existing
  // decision so re-opening reflects what’s currently set.
  const [analytics, setAnalytics] = useState(() => getConsent()?.analytics === true)

  // Let the footer’s "Cookie settings" link re-open the manage panel even
  // after a decision has been recorded.
  useEffect(() => {
    function onOpen() {
      setAnalytics(getConsent()?.analytics === true)
      setManageOpen(true)
      setShowBanner(true)
    }
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen)
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen)
  }, [])

  // Focus the heading whenever the banner appears (see the note above the
  // return). ⛔ MUST stay above the early return below: a hook after it ran
  // only while the banner showed, so re-opening it from the footer's Cookie
  // settings link changed the hook count and crashed the page (React #310),
  // which made withdrawing consent impossible.
  useEffect(() => {
    if (showBanner) headingRef.current?.focus()
  }, [showBanner])

  if (!showBanner) return null

  function dismiss() {
    setShowBanner(false)
    setManageOpen(false)
  }

  function onAcceptAll() {
    acceptAll()
    dismiss()
  }

  function onRejectAll() {
    rejectAll()
    dismiss()
  }

  function onSavePreferences() {
    setConsent({ analytics })
    dismiss()
  }

  // ⛔ BOTTOM RIGHT ON DESKTOP, and not centred. It used to be a centred
  // max-w-3xl slab that sat directly on top of the hero's call to action on
  // every page, on both breakpoints. The hero content is left aligned, so the
  // right corner is the one place a persistent card cannot collide with it.
  // Full width only on a phone, where there is no corner to hide in.
  // ⚠️ FOCUS LANDS HERE WHEN THE BANNER APPEARS. It mounts last in App.jsx, so
  // in DOM order it was tab stop 37 of 40 on desktop: a keyboard user met a
  // consent prompt on first load and had to traverse the nav, the hero, six FAQ
  // summaries and the whole footer before reaching "Reject optional". UK PECR
  // requires refusing to be as easy as accepting, and 36 tab presses is not.
  //
  // It is deliberately NOT aria-modal: this does not block the page, and
  // trapping someone in a consent prompt would be worse than burying it.

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:px-0 sm:pb-0"
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
    >
      <div className="mx-auto w-full rounded-card border border-hairline bg-surface shadow-[0_18px_50px_rgba(12,24,41,0.18)] sm:mx-0 sm:w-[380px]">
        <div className="p-5">
          <h2 ref={headingRef} tabIndex={-1} className="text-[17px] font-semibold text-ink outline-none">Cookies</h2>
          <p className="mt-2 text-[15px] leading-relaxed text-ink-muted">
            We use essential cookies to keep TempDash secure and running smoothly. With your
            permission, we’d also use light analytics to help us improve. No ads, ever. And we
            never sell your data.
          </p>

          {manageOpen && (
            <div className="mt-4 space-y-3">
              <Row
                title="Strictly necessary"
                subtitle="Required for the site to work (sign-in, security, this consent choice). Always on."
                locked
                checked
              />
              <Row
                title="Analytics"
                subtitle="First-party page views and time-on-page so we can improve the site. No third parties, no ads."
                checked={analytics}
                onChange={() => setAnalytics(v => !v)}
              />
            </div>
          )}

          {/* ⛔ The two real answers side by side, the two links on one row
              underneath. Three stacked full-width buttons made this card tall
              enough to bury the hero's call to action on a phone, which is the
              one thing the page exists to offer.
              ⛔ IDENTICAL STYLING (founder 2026-10-06). UK PECR / ICO: refusing
              must be as easy and as prominent as accepting, so both are the SAME
              outline pill at the SAME width, Reject first, and nothing in the
              banner is filled. Don't give Accept the action fill back, and don't
              add a filled button beside them. */}
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={onRejectAll} className={CHOICE}>
              Reject optional
            </button>
            <button type="button" onClick={onAcceptAll} className={CHOICE}>
              Accept analytics
            </button>
          </div>
          {/* Saving the toggles is the same weight as the two answers above,
              an outline pill, so the manage view adds no competing fill. */}
          {manageOpen && (
            <button type="button" onClick={onSavePreferences} className={`mt-2 w-full ${CHOICE}`}>
              Save preferences
            </button>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[14px]">
            <Link to="/legal/cookies" className={LINK}>
              Cookie Policy
            </Link>
            {!manageOpen && (
              <button type="button" onClick={() => setManageOpen(true)} className={LINK}>
                Cookie settings
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// A single category row in the manage panel. `locked` renders a static
// "always on" pill (necessary cookies can’t be turned off); otherwise a
// switch the visitor controls.
function Row({ title, subtitle, checked, onChange, locked }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-tile border border-hairline bg-surface-mute px-4 py-3">
      <div>
        <p className="text-[14px] font-semibold text-ink">{title}</p>
        <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">{subtitle}</p>
      </div>
      {locked ? (
        <span className="mt-0.5 shrink-0 rounded-full bg-hairline px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-ink">
          Always on
        </span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={title}
          onClick={onChange}
          className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition ${
            checked ? 'bg-action-fill' : 'bg-ink/25'
          }`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
              checked ? 'left-[22px]' : 'left-0.5'
            }`}
          />
        </button>
      )}
    </div>
  )
}
